import { Track } from "@/src/lib/tracking";
import type { Bridge } from "./bridge";
import { MessageType } from "./types";

type NativeVoicePermissionVerdict = "granted" | "denied" | "unknown";
export type NativeVoicePermissionResult = {
  verdict: NativeVoicePermissionVerdict;
  dialogShown: boolean;
};

type NativeVoicePayload = {
  status?: string;
  enabled?: boolean;
  dialogShown?: boolean;
  platform?: string;
  raw?: string;
};

function normalizeVoicePermissionData(data: unknown): NativeVoicePayload {
  if (data == null || typeof data !== "object") {
    return {};
  }
  let d = data as Record<string, unknown>;
  const inner = d.data;
  if (
    inner &&
    typeof inner === "object" &&
    !Array.isArray(inner) &&
    ("enabled" in inner ||
      "raw" in inner ||
      "status" in inner ||
      "platform" in inner)
  ) {
    d = inner as Record<string, unknown>;
  }
  return {
    status: typeof d.status === "string" ? d.status : undefined,
    enabled: typeof d.enabled === "boolean" ? d.enabled : undefined,
    dialogShown: typeof d.dialogShown === "boolean" ? d.dialogShown : undefined,
    platform: typeof d.platform === "string" ? d.platform : undefined,
    raw: typeof d.raw === "string" ? d.raw : undefined,
  };
}

function interpretNativeVoicePermissionPayload(
  data: unknown,
): NativeVoicePermissionVerdict {
  const p = normalizeVoicePermissionData(data);
  const status = p.status?.trim().toLowerCase() ?? "";
  const raw = p.raw?.trim().toLowerCase() ?? "";

  if (
    status === "granted" ||
    raw === "granted" ||
    raw === "authorized" ||
    status === "authorized"
  ) {
    return "granted";
  }
  if (p.enabled === true && raw !== "denied" && raw !== "restricted") {
    return "granted";
  }
  if (
    raw === "restricted" ||
    status === "denied" ||
    raw === "denied" ||
    status === "restricted"
  ) {
    return "denied";
  }
  if (raw === "notdetermined" || raw === "") {
    return "unknown";
  }
  if (p.enabled === false && (raw === "denied" || status === "denied")) {
    return "denied";
  }
  if (p.enabled === false) {
    return "unknown";
  }
  return "unknown";
}

function toPermissionResult(data: unknown): NativeVoicePermissionResult {
  const payload = normalizeVoicePermissionData(data);
  return {
    verdict: interpretNativeVoicePermissionPayload(data),
    dialogShown: payload.dialogShown === true,
  };
}

const DEFAULT_REQUEST_TIMEOUT_MS = 120_000;
const DEFAULT_CHECK_TIMEOUT_MS = 15_000;

export async function ensureNativeVoiceRecordingPermissionWithMeta(
  bridge: Bridge,
  options?: { timeoutMs?: number },
): Promise<NativeVoicePermissionResult> {
  if (!bridge.isInApp()) {
    return { verdict: "granted", dialogShown: false };
  }
  const timeoutMs =
    typeof options?.timeoutMs === "number" && options.timeoutMs > 0
      ? options.timeoutMs
      : DEFAULT_REQUEST_TIMEOUT_MS;
  try {
    const res = await bridge.request(
      MessageType.REQUEST_VOICE_PERMISSION,
      {},
      { timeoutMs },
    );
    return toPermissionResult(res.data);
  } catch (e) {
    Track.log(
      "warn",
      "bridge",
      "[voice] REQUEST_VOICE_PERMISSION failed; fallback to check",
      { cause: String(e) },
    );
    const verdict = await checkNativeVoiceRecordingPermission(bridge, {
      timeoutMs: DEFAULT_CHECK_TIMEOUT_MS,
    });
    return { verdict, dialogShown: false };
  }
}

/**
 * Query native microphone permission without showing a system dialog (when possible).
 */
async function checkNativeVoiceRecordingPermission(
  bridge: Bridge,
  options?: { timeoutMs?: number },
): Promise<NativeVoicePermissionVerdict> {
  if (!bridge.isInApp()) {
    return "granted";
  }
  const timeoutMs =
    typeof options?.timeoutMs === "number" && options.timeoutMs > 0
      ? options.timeoutMs
      : DEFAULT_CHECK_TIMEOUT_MS;
  try {
    const res = await bridge.request(
      MessageType.VOICE_PERMISSION,
      { action: "check" },
      { timeoutMs },
    );
    return interpretNativeVoicePermissionPayload(res.data);
  } catch (e) {
    Track.log("warn", "bridge", "[voice] VOICE_PERMISSION check failed", {
      cause: String(e),
    });
    return "unknown";
  }
}
